import React, { useCallback, useEffect, useState } from "react";
import routeJackpot from "./routes";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { routeAuth } from "../AuthPage";
import { getRequest, postRequest } from "../../services/requestController";
import { UserHeader } from "../../components/UserHeader/UserHeader";
import "./styles.scss";

import {
    Alert,
    Avatar,
    Badge,
    Button,
    Card,
    Col,
    Empty,
    Popconfirm,
    Progress,
    Row,
    Space,
    Spin,
    Statistic,
    Switch,
    Table,
    Tabs,
    Tag,
    Typography,
    notification,
} from "antd";


import {
    CheckCircleOutlined,
    HistoryOutlined,
    PoweroffOutlined,
    ReloadOutlined,
    StopOutlined,
    TrophyOutlined,
    UndoOutlined,
    UserOutlined,
} from "@ant-design/icons";

import HideForManager from "../../components/RoleGate/HideForManager";

const { Title, Text } = Typography;

const LEVEL_COLORS = { L1: "#1677ff", L2: "#52c41a", L3: "#faad14" };
const LEVEL_META = {
    L1: { label: "L1 — 1500 BRU", amount: 1500, count: 3, color: "#1677ff" },
    L2: { label: "L2 — 800 BRU",  amount: 800,  count: 6, color: "#52c41a" },
    L3: { label: "L3 — 400 BRU",  amount: 400,  count: 9, color: "#faad14" },
};

const ActiveCycleView = ({ cycle, onRefresh }) => {
    const [activeTab, setActiveTab] = useState("L1");

    const allPlayersMap = {
        L1: cycle.l1Players || [],
        L2: cycle.l2Players || [],
        L3: cycle.l3Players || [],
    };

    const candidateMap = {
        L1: allPlayersMap.L1.filter((p) => p.isJackpotSlot),
        L2: allPlayersMap.L2.filter((p) => p.isJackpotSlot),
        L3: allPlayersMap.L3.filter((p) => p.isJackpotSlot),
    };

    const gamesPlayed = cycle.gamesPlayed ?? 0;
    const gamesTotal = cycle.gamesTotal ?? 100;
    const cycleProgress = gamesTotal > 0 ? Math.round((gamesPlayed / gamesTotal) * 100) : 0;

    const drawnDrawings = cycle.drawings?.filter((d) => d.status === "drawn") || [];
    const totalDrawings = cycle.drawings?.length ?? 18;
    const totalCandidates =
        candidateMap.L1.length + candidateMap.L2.length + candidateMap.L3.length;

    const statusMap = {
        active:    { badge: "processing", text: "Идут игры",         color: "#1677ff" },
        drawing:   { badge: "warning",    text: "Готов к розыгрышу", color: "#faad14" },
        completed: { badge: "success",    text: "Завершён",          color: "#52c41a" },
    };
    const st = statusMap[cycle.status] || statusMap.active;

    const candidateColumns = [
        { title: "#", render: (_, __, i) => i + 1, width: 40 },
        {
            title: "Участник",
            dataIndex: "name",
            render: (name, rec) => (
                <Space size={8}>
                    <Avatar size="small" icon={<UserOutlined />} style={{ background: LEVEL_COLORS[activeTab] }} />
                    <Text strong style={{ fontSize: 13 }}>{name || rec.internalId}</Text>
                </Space>
            ),
        },
        {
            title: "ID",
            dataIndex: "internalId",
            width: 110,
            render: (v) => <Tag style={{ fontSize: 12 }}>{v}</Tag>,
        },
        {
            title: "Подгруппа",
            dataIndex: "subGroup",
            width: 90,
            render: (v) => <Tag color="purple">{v}</Tag>,
        },
        {
            title: "Заработано",
            dataIndex: "earnedPoints",
            width: 110,
            render: (v) => <Tag color="blue">{Math.round((v || 0) * 0.05)} bru</Tag>,
        },
        {
            title: "Приз",
            dataIndex: "jackpotAmount",
            width: 100,
            render: (v) =>
                v ? (
                    <Tag color="gold" icon={<TrophyOutlined />}>{v} BRU</Tag>
                ) : (
                    <Tag color="gold" icon={<TrophyOutlined />}>JP</Tag>
                ),
        },
    ];

    return (
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
            <div className="cycle-hero">
                <Row gutter={[24, 16]} align="middle">
                    <Col flex="auto">
                        <Space size={16} align="center">
                            <div
                                style={{
                                    width: 56, height: 56, borderRadius: 16,
                                    background: "linear-gradient(135deg,#faad14,#ff7a00)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    boxShadow: "0 6px 20px rgba(250,173,20,0.4)",
                                    fontSize: 28,
                                }}
                            >
                                🏆
                            </div>
                            <div>
                                <Title level={3} style={{ margin: 0, color: "#fff" }}>
                                    Цикл #{cycle.cycleNumber}
                                </Title>
                                <Badge status={st.badge} text={
                                    <Text style={{ color: st.color, fontWeight: 600 }}>{st.text}</Text>
                                } />
                            </div>
                        </Space>
                    </Col>
                    <Col>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={onRefresh}
                            ghost
                            style={{ color: "rgba(255,255,255,0.75)", borderColor: "rgba(255,255,255,0.2)" }}
                        >
                            Обновить
                        </Button>
                    </Col>
                </Row>

                <Row gutter={[16, 12]} style={{ marginTop: 20 }}>
                    {[
                        { label: "Игр сыграно",    value: `${gamesPlayed} / ${gamesTotal}` },
                        { label: "Участники L1",   value: `${candidateMap.L1.length} / ${LEVEL_META.L1.count}` },
                        { label: "Участники L2",   value: `${candidateMap.L2.length} / ${LEVEL_META.L2.count}` },
                        { label: "Участники L3",   value: `${candidateMap.L3.length} / ${LEVEL_META.L3.count}` },
                        { label: "JP-кандидатов",  value: `${totalCandidates}` },
                        { label: "Проведено",      value: `${drawnDrawings.length} / ${totalDrawings}` },
                    ].map((s, i) => (
                        <Col key={i} xs={12} sm={8} md={4}>
                            <Statistic
                                title={<Text style={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>{s.label}</Text>}
                                value={s.value}
                                valueStyle={{ color: "#fff", fontSize: 18 }}
                            />
                        </Col>
                    ))}
                </Row>

                <div style={{ marginTop: 16 }}>
                    <Progress
                        percent={cycleProgress}
                        strokeColor={{ "0%": "#faad14", "100%": "#ff7a00" }}
                        trailColor="rgba(255,255,255,0.12)"
                        format={(p) => <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>{p}%</Text>}
                    />
                </div>
            </div>

            <Card
                title={
                    <Space>
                        <TrophyOutlined style={{ color: "#faad14" }} />
                        <span>Участники джекпота по уровням</span>
                    </Space>
                }
                style={{ borderRadius: 16 }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    type="card"
                    items={["L1", "L2", "L3"].map((level) => {
                        const meta = LEVEL_META[level];
                        return {
                            key: level,
                            label: (
                                <Space size={6}>
                                    <Tag color={meta.color} style={{ margin: 0 }}>{level}</Tag>
                                    <Text style={{ fontSize: 12 }}>
                                        {meta.amount} BRU × {meta.count}
                                    </Text>
                                    <Text style={{ fontSize: 12 }}>
                                        ({candidateMap[level].length} 🏆)
                                    </Text>
                                </Space>
                            ),
                            children: (
                                <Card
                                    size="small"
                                    bordered={false}
                                    style={{ background: "#fafafa", borderRadius: 12 }}
                                    title={
                                        <Space>
                                            <TrophyOutlined style={{ color: "#faad14" }} />
                                            <span>Участники розыгрыша {level} — {meta.amount} BRU</span>
                                            <Tag color="gold">
                                                {candidateMap[level].length} / {meta.count} слотов
                                            </Tag>
                                            {allPlayersMap[level].length > 0 && (
                                                <Tag color="default" style={{ fontSize: 11 }}>
                                                    {allPlayersMap[level].length} сыграло
                                                </Tag>
                                            )}
                                        </Space>
                                    }
                                >
                                    {candidateMap[level].length === 0 ? (
                                        <Empty
                                            description={
                                                allPlayersMap[level].length > 0
                                                    ? `${allPlayersMap[level].length} игр сыграно в уровне ${level}, но никто не получил JP-слот`
                                                    : `Никто ещё не играл в уровне ${level}`
                                            }
                                            imageStyle={{ height: 40 }}
                                        />
                                    ) : (
                                        <Table
                                            size="small"
                                            pagination={{ pageSize: 10, size: "small", showSizeChanger: false }}
                                            dataSource={candidateMap[level].map((c, i) => ({ ...c, key: i }))}
                                            columns={candidateColumns}
                                        />
                                    )}
                                </Card>
                            ),
                        };
                    })}
                />
            </Card>

        </Space>
    );
};

const PastCyclesView = ({ token }) => {
    const [cycles, setCycles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await getRequest(`/api/admin/jackpot/cycles?page=${page}&limit=10`, token);
                if (res?.success) {
                    const d = res.data?.data ?? res.data;
                    setCycles(d?.cycles || []);
                    setTotal(d?.total || 0);
                }
            } finally { setLoading(false); }
        };
        load();
    }, [page, token, refreshKey]);

    const columns = [
        { title: "Цикл", dataIndex: "cycleNumber", render: (v) => <Text strong>#{v}</Text>, width: 80 },
        {
            title: "Статус",
            dataIndex: "status",
            render: (s) => {
                const m = { active: ["processing", "Активный"], drawing: ["warning", "Розыгрыш"], completed: ["success", "Завершён"] };
                const [st, txt] = m[s] || ["default", s];
                return <Badge status={st} text={txt} />;
            },
            width: 130,
        },
        {
            title: "Игр",
            render: (_, r) => `${r.gamesPlayed ?? 0} / ${r.gamesTotal ?? 100}`,
            width: 90,
        },
        {
            title: "Участники JP",
            render: (_, r) => {
                const jp =
                    (r.l1Players?.filter((p) => p.isJackpotSlot).length || 0) +
                    (r.l2Players?.filter((p) => p.isJackpotSlot).length || 0) +
                    (r.l3Players?.filter((p) => p.isJackpotSlot).length || 0);
                const total =
                    (r.l1Players?.length || 0) +
                    (r.l2Players?.length || 0) +
                    (r.l3Players?.length || 0);
                return (
                    <Space size={4}>
                        <Tag color="gold" icon={<TrophyOutlined />}>{jp}</Tag>
                        <Text type="secondary" style={{ fontSize: 11 }}>/ {total}</Text>
                    </Space>
                );
            },
            width: 130,
        },
        {
            title: "Победители",
            render: (_, r) => {
                const winners = r.drawings?.filter((d) => d.status === "drawn" && d.winner) || [];
                if (!winners.length) return <Text type="secondary">—</Text>;
                return (
                    <Space wrap>
                        {winners.slice(0, 3).map((d, i) => (
                            <Tag key={i} color={LEVEL_COLORS[d.level]} style={{ fontSize: 11 }}>
                                {d.amount} BRU · {d.winner?.name || d.winner?.internalId}
                            </Tag>
                        ))}
                        {winners.length > 3 && (
                            <Tag style={{ fontSize: 11 }}>+{winners.length - 3}</Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "Дата",
            dataIndex: "startedAt",
            render: (v) => v ? new Date(v).toLocaleDateString("ru-RU") : "—",
            width: 100,
        },
    ];

    return (
        <Card
            title={
                <Space>
                    <HistoryOutlined />
                    <span>История циклов ({total})</span>
                </Space>
            }
            extra={
                <Button icon={<ReloadOutlined />} size="small" onClick={() => setRefreshKey((k) => k + 1)}>
                    Обновить
                </Button>
            }
            style={{ borderRadius: 16 }}
        >
            <Table
                size="small"
                loading={loading}
                dataSource={cycles.map((c) => ({ ...c, key: c._id }))}
                columns={columns}
                pagination={{
                    current: page,
                    total,
                    pageSize: 10,
                    onChange: setPage,
                    showTotal: (t) => `Всего ${t}`,
                    size: "small",
                }}
                locale={{ emptyText: "Нет данных" }}
            />
        </Card>
    );
};


const JackpotPage = () => {
    const token = useSelector((state) => state.token);

    const [activeCycle, setActiveCycle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("active");
    const [jackpotEnabled, setJackpotEnabled] = useState(true);
    const [toggling, setToggling] = useState(false);
    const [resetting, setResetting] = useState(false);

    const loadActive = useCallback(async () => {
        setLoading(true);
        try {
            const [cycleRes, configRes] = await Promise.all([
                getRequest("/api/admin/jackpot/active", token),
                getRequest("/api/admin/jackpot/config", token),
            ]);
            if (cycleRes?.success) {
                const d = cycleRes.data?.data ?? cycleRes.data;
                setActiveCycle(d || null);
            }
            if (configRes?.success) {
                const cfg = configRes.data?.data ?? configRes.data;
                setJackpotEnabled(cfg?.jackpotEnabled ?? true);
            }
        } finally { setLoading(false); }
    }, [token]);

    useEffect(() => { loadActive(); }, [loadActive]);

    const handleToggle = async (checked) => {
        setToggling(true);
        try {
            const res = await postRequest("/api/admin/jackpot/toggle", { enabled: checked }, token);
            if (res?.success) {
                setJackpotEnabled(checked);
                notification.success({ message: checked ? "Джекпот-система включена" : "Джекпот-система выключена" });
            } else {
                notification.error({ message: "Ошибка", description: res?.error?.message || "Ошибка переключения" });
            }
        } finally { setToggling(false); }
    };

    const handleReset = async () => {
        setResetting(true);
        try {
            const res = await postRequest("/api/admin/jackpot/reset", {}, token);
            if (res?.success) {
                notification.success({ message: "Джекпот обнулён", description: "Новый цикл начнётся с первой игры" });
                loadActive();
            } else {
                notification.error({ message: "Ошибка", description: res?.error?.message || "Ошибка обнуления" });
            }
        } finally { setResetting(false); }
    };

    if (!token) return <Redirect to={routeAuth()} />;

    return (
        <div className="container jackpot-page">
            <UserHeader />

            <Row gutter={[16, 0]} align="middle" style={{ marginBottom: 20 }}>
                <Col flex="auto">
                    <Space align="center">
                        <Title level={3} style={{ margin: 0 }}>Джекпоты</Title>
                        <Tag color="purple" style={{ fontSize: 13, padding: "2px 10px" }}>AGGRESSIVE</Tag>
                    </Space>
                    <div>
                        <Text type="secondary">
                            Управление джекпот-системой и просмотр участников
                        </Text>
                    </div>
                </Col>
                <Col>
                    <Space>
                        <HideForManager>
                            <Space size={12} align="center">
                                <Space size={8} align="center" style={{
                                    padding: "6px 14px",
                                    borderRadius: 8,
                                    background: jackpotEnabled ? "rgba(82,196,26,0.08)" : "rgba(255,77,79,0.08)",
                                    border: `1px solid ${jackpotEnabled ? "rgba(82,196,26,0.3)" : "rgba(255,77,79,0.3)"}`,
                                }}>
                                    <Text style={{ fontSize: 13 }}>{jackpotEnabled ? "Включена" : "Выключена"}</Text>
                                    <Switch
                                        checked={jackpotEnabled}
                                        onChange={handleToggle}
                                        loading={toggling}
                                        checkedChildren={<CheckCircleOutlined />}
                                        unCheckedChildren={<StopOutlined />}
                                    />
                                </Space>

                                <Popconfirm
                                title="Обнулить джекпот?"
                                description="Текущий цикл будет завершён, следующая игра начнёт новый цикл с нуля."
                                onConfirm={handleReset}
                                okText="Обнулить"
                                cancelText="Отмена"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    icon={<UndoOutlined />}
                                    danger
                                    loading={resetting}
                                >
                                    Обнулить
                                </Button>
                                </Popconfirm>
                            </Space>
                        </HideForManager>

                        <Button icon={<ReloadOutlined />} onClick={loadActive} loading={loading}>
                            Обновить
                        </Button>
                    </Space>
                </Col>
            </Row>

            {!jackpotEnabled && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<PoweroffOutlined />}
                    message="Джекпот-система выключена"
                    description="Джекпот-слоты не будут назначаться новым играм. Существующие данные сохранены."
                    style={{ marginBottom: 16, borderRadius: 12 }}
                />
            )}

            <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                {["L1", "L2", "L3"].map((level) => {
                    const meta = LEVEL_META[level];
                    return (
                        <Col key={level} xs={24} md={8}>
                            <Card
                                size="small"
                                style={{
                                    borderRadius: 12,
                                    borderLeft: `4px solid ${meta.color}`,
                                }}
                            >
                                <Statistic
                                    title={<Text style={{ fontSize: 12 }}>{level}</Text>}
                                    value={meta.amount}
                                    suffix={<Text type="secondary" style={{ fontSize: 13 }}>BRU × {meta.count} шт</Text>}
                                    valueStyle={{ color: meta.color, fontSize: 24 }}
                                />
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            <Tabs
                activeKey={tab}
                onChange={setTab}
                size="large"
                items={[
                    {
                        key: "active",
                        label: (
                            <Space>
                                <TrophyOutlined />
                                <span>Активный цикл</span>
                                {activeCycle?.status === "drawing" && (
                                    <Badge count="!" style={{ backgroundColor: "#faad14" }} />
                                )}
                            </Space>
                        ),
                        children: loading ? (
                            <div style={{ textAlign: "center", padding: 60 }}>
                                <Spin size="large" />
                            </div>
                        ) : activeCycle ? (
                            <ActiveCycleView cycle={activeCycle} onRefresh={loadActive} />
                        ) : (
                            <Card style={{ borderRadius: 16 }}>
                                <Empty
                                    image={<TrophyOutlined style={{ fontSize: 64, color: "#d9d9d9" }} />}
                                    imageStyle={{ height: 64 }}
                                    description={
                                        <Space direction="vertical">
                                            <Text>Нет активного цикла джекпотов</Text>
                                            <Text type="secondary">
                                                Переключите алгоритм на AGGRESSIVE в настройках Игр
                                                и начните игры, чтобы запустить первый цикл
                                            </Text>
                                        </Space>
                                    }
                                />
                            </Card>
                        ),
                    },
                    {
                        key: "history",
                        label: (
                            <Space>
                                <HistoryOutlined />
                                <span>История</span>
                            </Space>
                        ),
                        children: tab === "history" ? <PastCyclesView token={token} /> : null,
                    },
                ]}
            />
        </div>
    );
};

export { routeJackpot };
export default JackpotPage;
